import { ReactNode } from 'react';

import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Modal, { ModalProps } from './CustomModal';
import { Button } from '@/components/atoms/new-button';

interface ConfirmationModalProps extends Omit<ModalProps, 'content'> {
  title: string;
  description: ReactNode | string;
  onConfirm: () => void;
  isLoading: boolean;
  confirmButtonText?: string;
  confirmButtonVariant?: 'primary' | 'secondary' | 'destructive';
}

const Description = ({ description }: { description: ReactNode | string }) => {
  if (typeof description === 'string') {
    return <p className="text-gray-600">{description}</p>;
  }
  return description;
};

export const ConfirmationModal = ({
  title,
  description,
  onConfirm,
  confirmButtonText = 'Delete',
  isLoading,
  confirmButtonVariant = 'destructive',
  ...props
}: ConfirmationModalProps) => {
  return (
    <Modal
      {...props}
      content={
        <div className="flex flex-col">
          <div className="border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          </div>
          <div className="px-6 py-4">
            <div className="mb-6">
              {isLoading ? (
                <div className="flex justify-center p-2 text-gray-500">
                  <FontAwesomeIcon icon={faCircleNotch} className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <Description description={description} />
              )}
            </div>
            <div className="flex justify-between gap-3">
              <Button variant="secondary" onClick={props.handleClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                data-testid="confirm-button"
                variant={confirmButtonVariant}
                onClick={onConfirm}
                disabled={isLoading}
              >
                {confirmButtonText}
              </Button>
            </div>
          </div>
        </div>
      }
    />
  );
};
